import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { CaptionService } from "../../../../utils/shared-services/caption.service";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { ProductGroup } from "../../models/price-change.model";
import { ReviewStoreNamesDialog } from "./review-store-names.component";
import { lastValueFrom } from "rxjs";
import { StoreNamesDialogComponent } from "./store-names-dialog.component";

@Component({
    selector: "price-history-dialog",
    templateUrl: "./src/app/features/price-change/templates/dialogs/review-price-changes.component.html",
    standalone: false
})
export class ReviewPriceChangeDialog implements OnInit {
    public changeData;
    public captions: Record<string, string> = this.captionService.captions;
    public orgProducts;

    public beforeAndAfterPrices = [];
    public groupedProducts: ProductGroup[] = [];
    public loading: boolean;
    expandedIndex: number | null = null;
    public headers = [this.captions.frontDescription, this.captions.stores, 'PriceA', 'PriceB', 'PriceC', 'PriceD', 'PriceE', 'PriceF', 'PriceG',
        'PriceH', 'PriceI', 'PriceJ', 'Price MOD'];
    constructor(@Inject(MAT_DIALOG_DATA) public data,
        @Inject(CaptionService) private captionService: CaptionService,
        @Inject(NgxIndexedDBService) private dbService: NgxIndexedDBService,
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(ChangeDetectorRef) private cd: ChangeDetectorRef

    ) {
    }

    async ngOnInit(): Promise<void> {
        this.loading = true
        await this.createTableArray();
        this.loading = false;
        this.cd.detectChanges();
    }

    async createTableArray(): Promise<void> {
        const orgProductsChunks = await lastValueFrom(this.dbService.getAll('orgProducts'));

        // Flatten into one array
        this.orgProducts = orgProductsChunks.flatMap((chunk: any) => chunk.products);

        // ✅ Build a dictionary for O(1) lookups
        const orgProductsMap = new Map<string, any>();
        for (const p of this.orgProducts) {
            orgProductsMap.set(`${p.StoreID}_${p.ProdNum}`, p);
        }

        for (const changedProduct of this.data.priceChange.changedPrices) {
            const key = `${changedProduct.StoreID}_${changedProduct.ProdNum}`;
            const original = orgProductsMap.get(key);
            if (!original) continue;

            const rollbackProduct: any = {
                ProdNum: changedProduct.ProdNum,
                StoreID: changedProduct.StoreID,
                Descript: original.Descript,
            };

            for (const [propName, value] of Object.entries(changedProduct)) {
                if (!propName.startsWith("Price")) continue;

                const numValue = Number(value); // ensures it’s numeric
                rollbackProduct[propName] = numValue;
                rollbackProduct[`${propName}Changed`] = numValue - Number(original[propName]);
            }

            this.beforeAndAfterPrices.push(rollbackProduct);
        }

        this.groupProducts(this.beforeAndAfterPrices);
    }


    groupProducts(products) {
        products.forEach(product => {
            const pgDescript = product.Descript?.trim().toLowerCase();
            let existingProductGroup = this.groupedProducts.find(productGroup => {
                return productGroup.Descript?.trim().toLowerCase() === pgDescript;
            });

            const storeName = this.data.selectedStores.find(store => store.id === product.StoreID)?.displayName;

            if (existingProductGroup) {
                existingProductGroup.products.push(product);

                // Avoid adding duplicate store names
                if (storeName && !existingProductGroup.storeNames.includes(storeName)) {
                    existingProductGroup.storeNames.push(storeName);
                }
            } else if (product.Descript) {
                const newProductGroup: ProductGroup = {
                    StoreID: product.StoreID,
                    Descript: product.Descript,
                    products: [product],
                    storeNames: storeName ? [storeName] : [],
                };
                this.groupedProducts.push(newProductGroup);
            }
        });
    }

    formatNumber(num) {
        if (num) {
            let formattedNum;
            if (num > 0) {
                formattedNum = `<span class="text-green-500">(+${num.toFixed(2)})</span>`;
            } else if (num < 0) {
                formattedNum = `<span class="text-red-500">(${num.toFixed(2)})</span>`;
            } else {
                formattedNum = num.toString(); // No color for zero
            }
            return formattedNum;
        } else return;
    }

    getDisplayValue(value: any) {
        return value ? value : '-';
    }

    toggleStoreNames(index: number): void {
        this.expandedIndex = this.expandedIndex === index ? null : index;
        this.cd.detectChanges();
    }

}
